import { z } from 'zod';
import { TexteBilingueSchema } from './textes.js';
/**
 * Largeurs de variante produites par le pipeline d'images (`sharp`, D15).
 * Le moteur ne fabrique pas d'image : il consomme des variantes déjà écrites
 * à côté du site, sous `/media/<id>/<largeur>.<ext>`.
 */
export const LARGEURS_VARIANTE = [320, 800, 1600, 2400];
export const LargeurVarianteSchema = z.union([
    z.literal(320),
    z.literal(800),
    z.literal(1600),
    z.literal(2400),
]);
/** Formats servis, du plus léger au plus compatible. */
export const FORMATS_VARIANTE = ['avif', 'webp'];
export const FormatVarianteSchema = z.enum(FORMATS_VARIANTE);
export const VarianteSchema = z.object({
    largeur: LargeurVarianteSchema,
    format: FormatVarianteSchema,
    /** Chemin public, relatif au domaine de la galerie. */
    url: z.string().startsWith('/'),
});
export const MediaSchema = z
    .object({
    id: z.string().min(1),
    /** Texte de remplacement, bilingue : les sites n'ont pas d'icône (2.5). */
    alt: TexteBilingueSchema,
    largeur: z.number().int().positive(),
    hauteur: z.number().int().positive(),
    /** Repli servi aux navigateurs qui n'ont ni AVIF ni WebP. */
    repliUrl: z.string().startsWith('/'),
    variantes: z.array(VarianteSchema).min(1),
    credit: TexteBilingueSchema.optional(),
})
    .refine((media) => media.variantes.some((v) => v.format === 'webp'), 'un média doit porter au moins une variante WebP');
/** Rapport largeur / hauteur, arrondi, pour réserver la place sans script. */
export function ratio(media) {
    return `${String(media.largeur)} / ${String(media.hauteur)}`;
}
/** Variantes d'un format, triées par largeur croissante. */
export function variantes(media, format) {
    return media.variantes
        .filter((v) => v.format === format)
        .slice()
        .sort((a, b) => a.largeur - b.largeur);
}
/** Attribut `srcset` d'un format, vide si le format n'est pas produit. */
export function srcset(media, format) {
    return variantes(media, format)
        .map((v) => `${v.url} ${String(v.largeur)}w`)
        .join(', ');
}
/** URL à servir en `src` : la variante 800 si elle existe, sinon le repli. */
export function urlPrincipale(media) {
    const webp = variantes(media, 'webp');
    const choisie = webp.find((v) => v.largeur === 800) ?? webp[webp.length - 1];
    return choisie?.url ?? media.repliUrl;
}
/** URL la plus large disponible : Open Graph et Twitter card. */
export function urlPartage(media) {
    const toutes = media.variantes.slice().sort((a, b) => a.largeur - b.largeur);
    const derniere = toutes[toutes.length - 1];
    return derniere?.url ?? media.repliUrl;
}
//# sourceMappingURL=medias.js.map